import React, { useEffect, useState } from 'react';
import AdminDashboardTemplate from '@/components/templates/AdminDashboardTemplate';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import SubscriberIcon from '@/assets/icons/subscriber.svg';
import NoSubscribeImg from '@/assets/icons/no-subscribe.png';
import { adminPanelService } from '@/services/admin-panel.service';
import SubscriberList from '@/components/organisms/SubscriberList';
import Pagination from '@/components/molecules/Pagination';
import SuspendUserModal from '@/components/molecules/SuspendUserModal';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { useNavigate } from 'react-router-dom';
import { useDeviceType } from '@/hooks/useDeviceType';
import EditSubscriptionModal from '@/components/molecules/EditSubscriptionModal';
import InfoModal from '@/components/molecules/InfoModal';
import reactivatePrimaryIcon from '@/assets/icons/reactivate-primary.svg';
import ConfirmModal from '@/components/molecules/ConfirmModal';
import { userService } from '@/services/user.service';

const AdminSubscribersPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'active' | 'unregistered' | 'suspended'>('active');
  const [recentSubscribers, setRecentSubscribers] = useState<any>([]);
  const [loadingRecentSubscribers, setLoadingRecentSubscribers] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedSubscriber, setSelectedSubscriber] = useState<any>(null);
  const [activeModal, setActiveModal] = useState<any>(null);
  const [loadingReactivateUser, setLoadingReactivateUser] = useState(false);
  const [showSuccessSuspendUser, setShowSuccessSuspendUser] = useState(false);
  const [bannedUntilMessage, setBannedUntilMessage] = useState<string>('');
  const [banInfo, setBanInfo] = useState<any>(null);

  const { isMobileView } = useDeviceType();

  const STATUS = {
    active: 'ACTIVE',
    unregistered: 'UNREGISTERED',
    suspended: 'BANNED'
  };

  const fetchRecentSubscribers = async (page = currentPage, limit = perPage) => {
    setLoadingRecentSubscribers(true);
    try {
      const { data: responseData } = await adminPanelService.getRecentSubscribers({
        day: 30,
        limit: isMobileView ? 100 : limit,
        page: page,
        status: STATUS[activeTab]
      });

      const { data: recentSubscribersData, totalCount: count, totalPages } = responseData;

      setRecentSubscribers(recentSubscribersData || []);
      setTotalPages(totalPages);
      setTotalCount(count);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRecentSubscribers(false);
    }
  };

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Subscriber',
          path: PAGE.ADMIN_SUBSCRIBERS.path
        }
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
    fetchRecentSubscribers(1, perPage);
  }, [activeTab, perPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchRecentSubscribers(page, perPage);
  };

  const handlePerPageChange = (perPage: number) => {
    setPerPage(perPage);
    setCurrentPage(1);
  };

  const handleChangeSubscription = (id: string) => {
    console.log('handleChangeSubscription', id);
    setActiveModal('handleChangeSubscription');
    setSelectedSubscriber(id);
  };

  const handleSuspend = (id: string) => {
    console.log('handleSuspend', id);
    const user = recentSubscribers.find((user: any) => user.id === id);
    console.log('userhandleSuspend', user);
    if (user?.banInfo && user?.banInfo?.ban_period) {
      setBanInfo(user?.banInfo);
    } else {
      setBanInfo(null);
    }
    setActiveModal('suspendUser');
    setSelectedSubscriber(id);
  };

  const displayBanUntilToast = (banUserResponseData: any) => {
    if (banUserResponseData?.banUntil) {
      const bannedUntilDate = new Date(banUserResponseData?.banUntil);
      const formattedDate = bannedUntilDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      console.log({ formattedDate });
      setShowSuccessSuspendUser(true);
      setBannedUntilMessage('Your account has been suspended Until ' + formattedDate);
      setActiveModal(null);
    } else {
      toast.success('User banned successfully');
      setShowSuccessSuspendUser(true);
      setBannedUntilMessage('User banned successfully');
      setActiveModal(null);
    }
  };

  const handleBanUser = async (banPeriod: number, timeUnit: string) => {
    console.log({ banPeriod, timeUnit, selectedSubscriber });
    try {
      const { data: banUserResponseData } = await adminPanelService.banUser(selectedSubscriber, {
        ban_period: banPeriod,
        time_unit: timeUnit
      });

      displayBanUntilToast(banUserResponseData);

      setSelectedSubscriber(null);
      setActiveModal(null);

      // Refresh the subscribers list
      fetchRecentSubscribers(currentPage, perPage);

      toast.success('User suspended successfully');
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubscriberClick = (id: string) => {
    console.log('handleSubscriberClick', id);
    navigate(`${PAGE.ADMIN_SUBSCRIBERS.path}/${id}`);
  };

  const handleUnregister = (id: string) => {
    console.log('handleUnregister', id);
    setActiveModal('unregisterUser');
    setSelectedSubscriber(id);
  };

  const handleReactivateUser = async () => {
    console.log('handleReactivateUser', selectedSubscriber);

    const user = recentSubscribers.find((user: any) => user.id === selectedSubscriber);
    console.log('userhandleReactivateUser', user);

    setLoadingReactivateUser(true);

    try {
      await adminPanelService.reactivateUserById(user?.firebaseUid);

      setSelectedSubscriber(null);
      fetchRecentSubscribers(currentPage, perPage);
      setActiveModal(null);

      toast.success('Reactivated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to reactivate. Please try again');
      setActiveModal(null);
    } finally {
      setLoadingReactivateUser(false);
    }
  };

  return (
    <AdminDashboardTemplate className="flex flex-col md:gap-4 !p-0 md:!p-4 overflow-hidden">
      <>
        <MobileAppBar title="Subscriber" />

        <div className=" md:card flex flex-col flex-1 md:space-y-3 overflow-hidden">
          <h1 className="text-2xl font-semibold hidden md:flex items-center gap-2">
            <img src={SubscriberIcon} alt="subscriber" width={24} height={24} />
            <span>Recent Subscribers</span>
          </h1>

          {/* Statsu Tabs */}
          <div className="flex border-b items-center gap-4">
            <button
              className={`text-base md:text-lg px-0 md:px-0 flex-1 md:flex-none py-2 md:text-secondary border-b-2 ${activeTab === 'active' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('active')}
              disabled={loadingRecentSubscribers}
            >
              Active
            </button>
            <button
              className={`text-base md:text-lg px-0 md:px-0 flex-1 md:flex-none py-2 md:text-secondary border-b-2 ${activeTab === 'unregistered' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('unregistered')}
              disabled={loadingRecentSubscribers}
            >
              Unregistered
            </button>
            <button
              className={`text-base md:text-lg px-0 md:px-0 flex-1 md:flex-none py-2 md:text-secondary border-b-2 ${activeTab === 'suspended' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('suspended')}
              disabled={loadingRecentSubscribers}
            >
              Suspended
            </button>
          </div>

          <div
            className={`overflow-y-auto w-full flex-1 p-6 pt-4 md:p-2 md:pt-2  md:overflow-y-auto flex ${recentSubscribers?.length === 0 ? 'items-center justify-center' : ''}`}
          >
            {loadingRecentSubscribers ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray">Loading...</p>
              </div>
            ) : recentSubscribers.length > 0 ? (
              <div className="w-full">
                <SubscriberList
                  items={recentSubscribers}
                  onChangeSubscription={handleChangeSubscription}
                  onSuspend={handleSuspend}
                  onItemClick={handleSubscriberClick}
                  onUnregister={handleUnregister}
                />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center">
                <img src={NoSubscribeImg} alt="no subscribe" />
                <p className="text-sm font-medium mt-3">There are no Subscription data to show</p>
                {activeTab === 'active' && (
                  <p className="text-xs text-secondary font-medium">
                    The data will be shown after user register to BatJet
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <footer className="hidden md:block min-h-7">
          {/* Pagination */}
          {recentSubscribers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loadingRecentSubscribers}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              totalCount={totalCount}
            />
          )}
        </footer>

        {activeModal === 'handleChangeSubscription' && (
          <EditSubscriptionModal
            isOpen={!!selectedSubscriber}
            onClose={() => {
              setSelectedSubscriber(null);
              setActiveModal(null);
            }}
            onButtonClick={() => {}}
          />
        )}

        {activeModal === 'suspendUser' && (
          <SuspendUserModal
            isOpen={!!selectedSubscriber}
            onClose={() => {
              setSelectedSubscriber(null);
              setActiveModal(null);
            }}
            onBanClick={handleBanUser}
            banInfo={banInfo}
          />
        )}

        {activeModal === 'unregisterUser' && (
          <ConfirmModal
            isOpen={activeModal === 'unregisterUser'}
            variant="success"
            title="Are you sure want to reactivate this user?"
            message="User will regain access to their account"
            buttonText={loadingReactivateUser ? 'Reactivating...' : 'Reactivate'}
            onConfirm={handleReactivateUser}
            icon={reactivatePrimaryIcon}
            onClose={() => {
              setActiveModal(null);
            }}
            loading={loadingReactivateUser}
          />
        )}

        {/* {showSuccessSuspendUser && (
          <InfoModal
            isOpen={showSuccessSuspendUser}
            variant="success"
            title={bannedUntilMessage}
            message="If you think you're not making any fraud. please contact support@batjet.com"
            buttonText="Close"
            onButtonClick={() => {
              setShowSuccessSuspendUser(false);
              setActiveModal(null);
              setBannedUntilMessage('');
              fetchRecentSubscribers(currentPage, perPage);
            }}
          />
        )} */}
      </>
    </AdminDashboardTemplate>
  );
};

export default AdminSubscribersPage;
